import axios from 'axios';
import { API_CONFIG, getCurrentConfig } from '../config/api';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS
});

// Request interceptor for logging and auth
apiClient.interceptors.request.use(
  (config) => {
    const envConfig = getCurrentConfig();
    
    if (envConfig.DEBUG) {
      console.log('🚀 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        fullURL: `${config.baseURL}${config.url}`
      });
    }
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error.message);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    const envConfig = getCurrentConfig();
    
    if (envConfig.DEBUG) {
      console.log('✅ API Response:', {
        status: response.status,
        url: response.config.url,
        method: response.config.method?.toUpperCase()
      });
    }
    return response;
  },
  (error) => {
    const envConfig = getCurrentConfig();
    
    if (envConfig.DEBUG) {
      console.error('❌ API Error:', {
        message: error.message,
        status: error.response?.status,
        url: error.config?.url
      });
    }
    
    // Handle common error scenarios
    if (error.response?.status === 404) {
      throw new Error('Resource not found');
    } else if (error.response?.status === 410) {
      throw new Error('This questionnaire is no longer available');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    }
    
    throw error;
  }
);

// Assessment API methods
export const assessmentAPI = {
  // Get all assessments
  getAssessments: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.ASSESSMENTS}?${params}`);
    return response.data;
  },

  // Get assessment details
  getAssessmentDetails: async (assessmentId) => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.ASSESSMENT_DETAILS(assessmentId));
    return response.data;
  },

  // Generate upload URL for file
  generateUploadUrl: async (filename, contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') => {
    const requestData = {
      filename,
      content_type: contentType
    };
    
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.UPLOAD_URL, requestData);
    return response.data;
  },

  // Upload file to S3 using presigned URL
  uploadFile: async (uploadUrl, file) => {
    const headers = {
      'Content-Type': file.type
    };
    
    const response = await axios.put(uploadUrl, file, { headers });
    return response;
  },

  // Trigger analysis for assessment
  triggerAnalysis: async (assessmentId, questionnaireId) => {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.TRIGGER_ANALYSIS(assessmentId), {
      questionnaire_id: questionnaireId
    });
    return response.data;
  }
};

// Questionnaire API methods
export const questionnaireAPI = {
  // Get questionnaire for operators
  getQuestionnaire: async (questionnaireId) => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.QUESTIONNAIRE(questionnaireId));
    return response.data;
  },

  // Submit operator response
  submitResponse: async (questionnaireId, responses, sessionId = null) => {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.SUBMIT_RESPONSE(questionnaireId), {
      responses,
      session_id: sessionId || generateSessionId()
    });
    return response.data;
  },

  // Collect responses (disable questionnaire)
  collectResponses: async (questionnaireId) => {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.COLLECT_RESPONSES(questionnaireId));
    return response.data;
  },

  // Get analysis results
  getAnalysisResults: async (questionnaireId) => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.ANALYSIS_RESULTS(questionnaireId));
    return response.data;
  },

  // Generate follow-up questionnaire
  generateFollowupQuestionnaire: async (questionnaireId) => {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.GENERATE_FOLLOWUP(questionnaireId));
    return response.data;
  }
};

// Utility functions
export const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Error handling utilities
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    return {
      type: 'server_error',
      status,
      message: data?.error || data?.message || 'Server error occurred',
      details: data
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      type: 'network_error',
      message: 'Network error. Please check your connection.',
      details: error.request
    };
  } else {
    // Something else happened
    return {
      type: 'unknown_error',
      message: error.message || 'An unexpected error occurred',
      details: error
    };
  }
};

// Health check function
export const healthCheck = async () => {
  try {
    const response = await apiClient.get('/api/health');
    return { status: 'healthy', data: response.data };
  } catch (error) {
    return { status: 'unhealthy', error: handleApiError(error) };
  }
};

export default apiClient;